import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import _axios from "../../../utils/axios";
import { USER } from "../../../utils/settings";

import SyntaxHighlighter from "react-syntax-highlighter";
import { googlecode as shStyle } from "react-syntax-highlighter/dist/esm/styles/hljs";
import styles from '../../../styles/base_components/Page.module.scss';

// MUI
import {
    Box,
    Stack,
    Chip,
    Typography,
    Divider,
    Card,
    CardHeader,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export function isJson(str) {
    if (typeof str !== "string") return false;
    try {
        JSON.parse(str);
        return true;
    } catch {
        return false;
    }
}

function tsToLocal(ts) {
    const ms = ts < 10000000000 ? ts * 1000 : ts;
    return new Date(ms).toLocaleString();
}

export default function SavedApiResponse() {
    const { refId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [record, setRecord] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const fetchResponse = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await _axios.get(USER.SAVED_RESPONSES_PATH, { params: { ref_id: refId } });
                const data = res?.data?.data ?? null;
                if (!cancelled) setRecord(data);
            } catch (err) {
                console.error("Error fetching saved API response:", err);
                if (!cancelled) setError(err?.message || "Failed to load");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchResponse();
        return () => { cancelled = true; };
    }, [refId]);

    const requestText = useMemo(() => record?.request ? JSON.stringify(record.request, null, 4) : null, [record]);
    const responseText = useMemo(() => record?.response ? record.response : null, [record]);
    const responseLanguage = useMemo(() => responseText && isJson(responseText) ? "json" : "text", [responseText]);

    if (loading) return <div className={styles.page}><Typography>Loading…</Typography></div>;
    if (error) return <div className={styles.page}><Typography color="error">Error: {error}</Typography></div>;
    if (!record) return <div className={styles.page}><Typography>No data for ref {refId}</Typography></div>;

    const { _id, ms, ms_version, fetcher, ts } = record;

    return (
        <Box sx={{
            minHeight: '100dvh', alignContent: 'start',
            p: 2, display: "grid", gap: 2, '& pre, & code, & .hljs': {
                fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace !important',
                fontWeight: '400 !important',
            },
            '& pre *, & code *, & .hljs *': {
                fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace !important',
                fontWeight: '400 !important',
            },
        }}>
            <Typography variant="h5">Saved API Response</Typography>

            {/* Header chips */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`ref: ${refId || _id}`} size="small" />
                {ms && <Chip label={`ms: ${ms}${ms_version ? `@${ms_version}` : ""}`} size="small" />}
                {fetcher && <Chip label={`fetcher: ${fetcher}`} size="small" />}
                {typeof ts !== "undefined" && <Chip label={`ts: ${tsToLocal(ts)}`} size="small" />}
            </Stack>

            <Divider />

            {requestText && (
                <Card variant="outlined">
                    <CardHeader title="Request" />
                    <CardContent sx={{ p: 0 }}>
                        <SyntaxHighlighter language="json" style={shStyle} wrapLongLines>
                            {requestText}
                        </SyntaxHighlighter>
                    </CardContent>
                </Card>
            )}

            {responseText && (
                <Card variant="outlined">
                    <CardHeader title="Response" />
                    <CardContent sx={{ p: 0 }}>
                        <SyntaxHighlighter language={responseLanguage} style={shStyle} wrapLongLines>
                            {responseText}
                        </SyntaxHighlighter>
                    </CardContent>
                </Card>
            )}

            {/* Collapsed by default */}
            <Accordion defaultExpanded={false} elevation={0} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>Raw Saved Record</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <SyntaxHighlighter language="json" style={shStyle} wrapLongLines>
                        {JSON.stringify(record, null, 4)}
                    </SyntaxHighlighter>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
